import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';

HighchartsMore(Highcharts);
SolidGauge(Highcharts);

export type MvxGaugeBand = { color: string; from: number; to: number };

type Props = {
  /** MVX score (0–100) — the needle position. */
  value: number;
  /** Level word shown in blue, e.g. "High" / "Elevated" / "Borderline" / "Optimal". */
  category: string;
  /** Always-there black line above the level, e.g. "Metabolic Vulnerability is". */
  text: string;
  /** Zone bands on the 0–100 scale (green → red around the decision limit). */
  plotBands: MvxGaugeBand[];
  /** Score values to label on the arc (e.g. the zone boundaries + endpoints). */
  ticks?: number[];
  /** Top of the gauge scale. Defaults to 100 — but the MVX score can exceed 100. */
  max?: number;
  chartContainerId?: string;
};

/**
 * MVX hero gauge — the exact Highcharts solid-gauge treatment the app uses for
 * every Insights gauge (mirrors LabsmartsGuage), so it matches the Glucose gauge
 * look: a colored 0–100 arc + needle with "{text}" (black) over
 * "{category} ({value})" (blue) beneath it. Only the plot bands (MVX zones) and
 * the no-percent value differ from the shared gauge.
 */
export class MvxScoreGauge extends React.Component<Props> {
  createOptions(): Highcharts.Options {
    const { value, category, text, plotBands, ticks, max } = this.props;
    const showTicks = !!(ticks && ticks.length);
    const scaleMax = max && max > 0 ? max : 100;
    return {
      title: { text: '' },
      chart: {
        type: 'solidgauge',
        height: 200,
        width: 260,
        // Exact same footprint as the Glucose gauge (LabsmartsGuage), so the arc
        // is the same size and sits in the same spot.
        margin: [-20, 0, -40, 0],
        backgroundColor: 'transparent',
      },
      pane: {
        center: ['50%', '60%'],
        size: '100%',
        startAngle: -90,
        endAngle: 90,
        background: [{ innerRadius: '60%', outerRadius: '100%', shape: 'arc' }],
      },
      yAxis: {
        min: 0,
        max: scaleMax,
        lineWidth: 0,
        // No tick dashes anywhere — major OR minor — just the number labels at the
        // zone boundaries. (tickWidth:0 alone leaves the gauge's minor ticks drawn.)
        tickWidth: 0,
        tickLength: 0,
        minorTickWidth: 0,
        minorTickLength: 0,
        // Numbers sit just inside the colored ring, so they never widen the
        // 260px chart the way outside labels (0 / 100 at the arc ends) would.
        tickPositions: showTicks ? ticks : [],
        labels: {
          enabled: showTicks,
          // Just inside the ring — the arc fills the chart (glucose footprint), so
          // there's no room outside it within the panel width.
          distance: -15,
          style: { fontSize: '10.5px', color: '#3a3a3a', fontWeight: '600', textOutline: '2px #ffffff' },
        },
        plotBands: plotBands.map(b => ({
          color: b.color,
          from: b.from,
          to: b.to,
          innerRadius: '60%',
          outerRadius: '100%',
        })),
      },
      series: [
        {
          type: 'gauge',
          data: [value],
          dataLabels: {
            useHTML: true,
            format: `<div class="anemiaStatusGuageLabelContainer">${text}<br/>
                        <div class="anemiaStatusGuageLabel">
                            ${category} (${value})
                        </div>
                     </div>`,
            borderWidth: 0,
          },
        } as Highcharts.SeriesGaugeOptions,
      ],
      credits: { enabled: false },
      exporting: { enabled: false },
      tooltip: { enabled: false },
    };
  }

  render() {
    return (
      <HighchartsReact
        highcharts={Highcharts}
        options={this.createOptions()}
        containerProps={{ id: this.props.chartContainerId }}
      />
    );
  }
}

export default MvxScoreGauge;
